import { Controller } from "@hotwired/stimulus";

// import './style.css';
import { defaults as defaultControls } from 'ol/control/defaults.js';
import ScaleLine from 'ol/control/ScaleLine.js';
import { Point } from 'ol/geom.js';
import { Feature, Map, View } from 'ol/index.js';
import { Tile as TileLayer, Vector as VectorLayer } from 'ol/layer.js';
import { useGeographic } from 'ol/proj.js';
import { Vector as VectorSource } from 'ol/source.js';
import { log } from "../../javascript/utils/logger";
import { customizations } from "../../javascript/utils/stoked_values";

import ls from 'localstorage-slim';
import StadiaMaps from 'ol/source/StadiaMaps.js';

import { markerStyle } from "../../javascript/utils/map_marker";

export default class extends Controller {
  connect() {
    log("advocate map connect")
    useGeographic();

    this.defaultPinColor = customizations.getValue("default-pin-color") || "#e53b2a"

    if (this.isPreview) {
      log("skip rendering for preview")
    } else {
      const config = this.buildConfig()
      log("mounting map", config)
      this.initializeMap(config)
    }
  }

  disconnect() {
    log("advocate map disconnect")
    this.map.setTarget(null)
    this.map.getLayers().forEach((layer) => this.map.removeLayer(layer))
    this.map = null
  }

  async initializeMap({ mapElement, mapID, saveCenter, center, zoom, features, mapStyle }) {
    const map = new Map({
      target: mapElement,
      layers: [
        new TileLayer({
          source: new StadiaMaps({
            layer: mapStyle, retina: true,
          }),
        }),
        new VectorLayer({
          source: new VectorSource({
            features,
          }),
        }),
      ],
      view: new View({
        center: [center.lng, center.lat],
        zoom,
        minZoom: 3,
        enableRotation: false
      }),
      controls: defaultControls().extend([new ScaleLine({ units: 'us', })])
    })
    this.map = map

    // handle marker click
    map.on('singleclick', (event) => {
      if (map.hasFeatureAtPixel(event.pixel)) {
        const features = map.getFeaturesAtPixel(event.pixel)
        const path = "/" + features[0].get('identifier')

        if (event.originalEvent.metaKey) {
          window.open(path, '_blank').focus();
        } else {
          Turbo.visit(path)
        }
      }
    })

    // ensure mouse shows a click pointer when hovering on markers
    map.on('pointermove', (event) => {
      var hit = map.hasFeatureAtPixel(event.pixel);
      map.getViewport().style.cursor = hit ? 'pointer' : '';
    })


    map.on('moveend', (event) => {
      const view = map.getView()
      const viewCenter = view.getCenter()
      const center = { lat: viewCenter[1], lng: viewCenter[0] }
      const zoom = view.getZoom()

      // If enabled, save map center + zoom
      if (mapID && saveCenter) {
        ls.set(`advocate-map.${mapID}`, { center, zoom }, { ttl: 3600 })
      }

      // publish map updated event
      document.dispatchEvent(
        new CustomEvent("advocate-map-updated", { detail: { mapID, center, zoom } })
      )
    })

    // map state tracking
    map.on("movestart", () => this.updateLoadingAttr("movestart", "true"))
    map.on("loadstart", () => this.updateLoadingAttr("loadstart", "true"))
    map.on("rendercomplete", () => this.updateLoadingAttr("rendercomplete", "false"))
  }

  get isPreview() {
    return document.documentElement.hasAttribute('data-turbolinks-preview')
  }

  get isDarkMode() {
    return document.querySelector('html').classList.contains("dark")
  }

  buildConfig() {
    const mapElement = this.element
    const mapID = mapElement.id
    const saveCenter = mapElement.dataset.saveCenter == "true"
    const markers = JSON.parse(mapElement.dataset.markers)

    var center = JSON.parse(mapElement.dataset.center)
    var zoom = JSON.parse(mapElement.dataset.zoomLevel)

    const savedData = ls.get(`advocate-map.${mapID}`)

    if (mapID && saveCenter && savedData) {
      center = savedData.center
      zoom = savedData.zoom
    }

    const features = markers.map((marker) => this.buildFeature(marker))
    const mapStyle = this.isDarkMode ? 'alidade_smooth_dark' : 'osm_bright'

    return { mapElement, mapID, saveCenter, center, zoom, markers, features, mapStyle }
  }

  buildFeature({ lat, lng, identifier, color, ...data }) {
    const feature = new Feature({
      identifier,
      ...data,
      geometry: new Point([lng, lat])
    })

    color = color || this.defaultPinColor
    feature.setStyle(markerStyle({ color }))

    feature.setId(`advocate-point-${identifier}`)
    return feature
  }

  updateLoadingAttr(event, value) {
    this.element.setAttribute("map-loading", value)
  }
}
